import { Directive, EmbeddedViewRef, Host, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { PermissionService } from '@app/services/permission/permission.service';
import { concatMap, distinctUntilChanged, map } from 'rxjs/operators';

function id(a){ return a; }

@Directive({
	selector: '[appIfPermission]',
})
export class IfPermissionDirective implements OnDestroy {
	private readonly subscription = new Subscription();

	private readonly permission$ = new Subject<string[]>();
	private readonly some$ = new BehaviorSubject<boolean>(false);
	private readonly elseTemplate$ = new BehaviorSubject<TemplateRef<undefined> | null>(null);

	@Input('appIfPermission')
	public set permission(value: string | string[] | undefined) {
		if (typeof value === 'string') {
			value = [value];
		}

		this.permission$.next(value ?? []);
	}

	@Input('appIfPermissionSome')
	public set some(value: boolean) {
		this.some$.next(value);
	}

	// eslint-disable-next-line @angular-eslint/no-input-rename
	@Input('appIfPermissionElse')
	public set elseTemplate(value: TemplateRef<undefined>) {
		this.elseTemplate$.next(value);
	}

	constructor(@Host() template: TemplateRef<void>, viewContainerRef: ViewContainerRef, permissionService: PermissionService,
	) {
		let view: EmbeddedViewRef<void> | undefined = undefined;

		const hasPerimssions$ =
			this.permission$.pipe(
				concatMap(permissions => combineLatest(permissions.map(permission => permissionService.hasPermission$(permission))))
			);

		const hasPermission$ = combineLatest([
			hasPerimssions$,
			this.some$
		]).pipe(
			map(([hasPermissions, some]) => some ? hasPermissions.some(id):hasPermissions.every(id)),
			distinctUntilChanged(),
		);

		this.subscription.add(
			combineLatest([
				hasPermission$,
				this.elseTemplate$
			]).subscribe(([hasPermission, elseTemplate]) => {
				view?.destroy();

				if (hasPermission) {
					view = viewContainerRef.createEmbeddedView(template);
				} else if (elseTemplate != null) {
					view = viewContainerRef.createEmbeddedView(elseTemplate);
				}
			}),
		);

	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
